/**
 * Request deduplication utility
 * 
 * Prevents duplicate in-flight requests by caching promises for identical requests.
 * If the same request is made while one is in-flight, it returns the same promise.
 */

type RequestKey = string;

interface RequestCacheEntry<T> {
  promise: Promise<T>;
  timestamp: number;
  evictTimer?: ReturnType<typeof setTimeout>;
}

const requestCache = new Map<RequestKey, RequestCacheEntry<any>>();

// Maximum cache age (stale entries cleanup) - 5 minutes
const MAX_CACHE_AGE_MS = 5 * 60 * 1000;
// Safety: evict never-settling in-flight promises so future calls can retry.
const MAX_IN_FLIGHT_MS = 60 * 1000;

/**
 * Generates a cache key from request parameters
 */
function generateCacheKey(
  url: string,
  method: string = 'GET',
  body?: unknown,
): RequestKey {
  const parts = [method.toUpperCase(), url];
  
  if (body !== undefined && method.toUpperCase() !== 'GET') {
    try {
      parts.push(JSON.stringify(body));
    } catch {
      // If body can't be stringified, use a hash placeholder
      parts.push(String(body));
    }
  }
  
  return parts.join('|');
}

/**
 * Normalizes URL by sorting query parameters for consistent cache keys
 */
function normalizeUrl(url: string): string {
  try {
    const [base, query] = url.split('?');
    if (!query) return url;
    
    const params = new URLSearchParams(query);
    // Sort parameters for consistent keys
    const sortedParams = Array.from(params.entries())
      .sort(([a], [b]) => a.localeCompare(b));
    
    const normalizedQuery = new URLSearchParams(sortedParams).toString();
    return normalizedQuery ? `${base}?${normalizedQuery}` : base;
  } catch {
    return url;
  }
}

/**
 * Cleans up stale cache entries
 */
function cleanupStaleEntries(): void {
  const now = Date.now();
  // Convert entries to array for compatibility with older TypeScript targets
  Array.from(requestCache.entries()).forEach(([key, entry]) => {
    if (now - entry.timestamp > MAX_CACHE_AGE_MS) {
      requestCache.delete(key);
    }
  });
}

/**
 * Deduplicates requests by caching in-flight promises
 * 
 * @param url - Request URL (will be normalized)
 * @param fetcher - Function that performs the actual request
 * @param method - HTTP method (default: 'GET')
 * @param body - Request body (for non-GET requests)
 * @returns Promise that resolves/rejects the same as the fetcher
 */
export function dedupeRequest<T>(
  url: string,
  fetcher: () => Promise<T>,
  method: string = 'GET',
  body?: unknown,
): Promise<T> {
  // Normalize URL to handle query parameter order differences
  const normalizedUrl = normalizeUrl(url);
  const cacheKey = generateCacheKey(normalizedUrl, method, body);
  
  // Clean up stale entries periodically (every 10th call)
  if (requestCache.size > 0 && Math.random() < 0.1) {
    cleanupStaleEntries();
  }
  
  // Check if there's an in-flight request
  const existing = requestCache.get(cacheKey);
  if (existing) {
    return existing.promise;
  }
  
  // Create new request promise
  const entry: RequestCacheEntry<T> = {
    promise: Promise.resolve().then(fetcher),
    timestamp: Date.now(),
  };

  // If a request hangs (e.g. network stall without abort), don't permanently poison this key.
  entry.evictTimer = setTimeout(() => {
    requestCache.delete(cacheKey);
  }, MAX_IN_FLIGHT_MS);

  entry.promise = entry.promise.finally(() => {
    if (entry.evictTimer) {
      clearTimeout(entry.evictTimer);
    }
    // Remove from cache when complete (success or error)
    requestCache.delete(cacheKey);
  });
  
  // Store in cache
  requestCache.set(cacheKey, entry);
  
  return entry.promise;
}

/**
 * Clears all cached requests (useful for testing or forced refresh)
 */
export function clearRequestCache(): void {
  requestCache.clear();
}

/**
 * Gets the current cache size (useful for debugging)
 */
export function getRequestCacheSize(): number {
  return requestCache.size;
}
